/**
 * Optimistisches Locking (CRM 2.0) — Client-Seite.
 *
 * Vertrag mit der API (App\Support\OptimisticLock):
 *   - Lese-Endpunkte liefern `updated_at` (ISO-8601).
 *   - Beim Speichern schickt das Formular genau diesen Wert als `updated_at`
 *     zurück. Hat jemand anderes den Datensatz inzwischen gespeichert,
 *     antwortet die API mit 409 `E_STALE_WRITE` und dem aktuellen Stand
 *     unter `meta.current` — nichts wird überschrieben.
 *
 * Dieses Modul übersetzt die 409-Antwort in einen typisierten Fehler, damit
 * Formulare den Konflikt gezielt anzeigen und den aktuellen Stand anbieten
 * können (statt „Speichern fehlgeschlagen").
 */

export const STALE_WRITE_CODE = 'E_STALE_WRITE';

export class StaleWriteError<T = unknown> extends Error {
  readonly code = STALE_WRITE_CODE;
  constructor(public readonly current: T | null) {
    super('stale_write');
    this.name = 'StaleWriteError';
  }
}

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: {
      errors?: Array<{ code?: string; message?: string }> | null;
      meta?: { current?: unknown } | unknown[];
    };
  };
}

/**
 * Erkennt die 409-`E_STALE_WRITE`-Antwort. Gibt den typisierten Fehler zurück,
 * sonst null (dann normal weiterbehandeln).
 */
export function toStaleWriteError<T = unknown>(e: unknown): StaleWriteError<T> | null {
  if (e instanceof StaleWriteError) return e as StaleWriteError<T>;
  const err = e as AxiosLikeError;
  const res = err?.response;
  if (!res || res.status !== 409) return null;
  const code = res.data?.errors?.[0]?.code;
  if (code !== STALE_WRITE_CODE) return null;
  const meta = res.data?.meta;
  const current = meta && !Array.isArray(meta) ? (meta.current as T | undefined) ?? null : null;
  return new StaleWriteError<T>(current);
}

/** Wirft den typisierten Fehler bei Konflikt, reicht alles andere unverändert weiter. */
export function rethrowStaleWrite<T = unknown>(e: unknown): never {
  const stale = toStaleWriteError<T>(e);
  throw stale ?? e;
}
